







































































































import {
    defineComponent,
    computed,
    ref,
    PropType,
    watch
} from "@vue/composition-api";
import AttachedDeviceModel from "@/models/AttachedDeviceModel";
import { alarm, sensor } from "@/store";
import UserSettingsViewModel from "../../../models/UserSettingsViewModel";

export default defineComponent({
    components: {
        SensorHeader: () =>
            import(/* webpackChunkName: "components" */ "./SensorHeader.vue"),
        SensorPanelLayout: () =>
            import(/* webpackChunkName: "components" */ "./SensorPanelLayout.vue"),
        CurrentDialog: () =>
            import(/* webpackChunkName: "components" */ "../Dialogs/CurrentDialog.vue"),
        EditCurrentDialog: () =>
            import(/* webpackChunkName: "components" */ "../Dialogs/EditCurrentDialog.vue"),
        ConfirmDialog: () =>
            import(/* webpackChunkName: "settings" */ "../Dialogs/ConfirmDialog.vue")
    },
    props: {
        value: { type: Object as PropType<AttachedDeviceModel> },
        threshold: { type: Number, required: true }
    },
    setup(props, { emit }) {
        const showCurrentDialog = ref(false);
        const showCurrentEditThresholdDialog = ref(false);
        const showCalibrateDialog = ref(false);
        const showCalibrateError = ref(false);
        const showCalibrateSuccess = ref(false);
        const calibrateErrorMessage = ref("Calibration request failed.");
        const isCalibrating = ref(false);
        const calibrateTimeoutId = ref<number | null>(null);
        const calibrateStatusCheckId = ref<number | null>(null);
        const CALIBRATE_TIMEOUT_MS = 90000;
        const CALIBRATE_STATUS_CHECK_MS = 45000;
        const rawCurrent = computed(() => {
            const alarmStatus = alarm.alarmStatus;
            const vSupplies = alarmStatus && alarmStatus.VSupplies;
            const raw = vSupplies && vSupplies[0] && vSupplies[0].I1;
            return raw != null ? raw : null;
        });

        const currentValue = computed(() => {
            const value = rawCurrent.value;
            const num = parseFloat(value != null ? value : "");
            return isNaN(num) ? null : num;
        });

        const connected = computed(() => {
            // Match alarm module logic: consider connected if absolute value of current < 50
            return currentValue.value !== null && Math.abs(currentValue.value) < 50;
        });

        const paired = computed<boolean>(() =>
            !!props.value?.attachedDeviceId
        );

        const pumpStatus = computed(() => {
            // pumpIsRunning = true means current >= threshold
            return currentValue.value !== null && currentValue.value >= props.threshold;
        });

        const currentDisplay = computed(() => {
            if (!connected.value) return "—.- A";
            return `${currentValue.value.toFixed(1)} A`;
        });

        const requestAdcCalibration = async () => {
            const result = await sensor.RequestAdcCalibration(0); // 0 is channel for current sensor
            if (result?.ok) {
                isCalibrating.value = true;
                if (calibrateTimeoutId.value !== null) {
                    clearTimeout(calibrateTimeoutId.value);
                }
                if (calibrateStatusCheckId.value !== null) {
                    clearTimeout(calibrateStatusCheckId.value);
                }
                calibrateTimeoutId.value = window.setTimeout(() => {
                    isCalibrating.value = false;
                    calibrateErrorMessage.value = "Calibration timed out.";
                    showCalibrateError.value = true;
                    calibrateTimeoutId.value = null;
                }, CALIBRATE_TIMEOUT_MS);
                calibrateStatusCheckId.value = window.setTimeout(() => {
                    if (isCalibrating.value) {
                        alarm.getStatus();
                    }
                    calibrateStatusCheckId.value = null;
                }, CALIBRATE_STATUS_CHECK_MS);
            } else {
                calibrateErrorMessage.value = result?.message || "Calibration request failed.";
                showCalibrateError.value = true;
            }
        };

        watch(currentValue, (value) => {
            if (isCalibrating.value && value !== null && Math.abs(value) <= 0.1) {
                isCalibrating.value = false;
                if (calibrateTimeoutId.value !== null) {
                    clearTimeout(calibrateTimeoutId.value);
                    calibrateTimeoutId.value = null;
                }
                if (calibrateStatusCheckId.value !== null) {
                    clearTimeout(calibrateStatusCheckId.value);
                    calibrateStatusCheckId.value = null;
                }
                showCalibrateSuccess.value = true;
            }
        });

        const handleOnSaveCurrentThreshold = (model: UserSettingsViewModel) => {
            props.threshold = model.bilgeThreshold;
            emit("OnNewCurrentThreshold", model);
        };

        return {
            connected,
            paired,
            pumpStatus,
            currentDisplay,
            showCurrentDialog,
            showCalibrateDialog,
            showCalibrateError,
            showCalibrateSuccess,
            calibrateErrorMessage,
            isCalibrating,
            requestAdcCalibration,
            showCurrentEditThresholdDialog,
            handleOnSaveCurrentThreshold
        };
    }
});
